import { Button } from "@msuite/ui/button";
import { DateInput } from "@msuite/ui/date-input";
import type { UseLoading } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { usePopoverContext } from "@msuite/ui/popover";
import { responseHandler } from "@msuite/ui/utils";
import { moment } from "@msuite/utils/date";
import { type PostCopyDaySchema, postCopyDaySchema } from "@msuite/utils/schemas";
import { useForm } from "react-hook-form";
import { client } from "@/hono";

/** Props Interface */
interface DailyCopyDayPopoverProps {
	planId: string;
	date: string;
	copyLoader: UseLoading;
}

export function DailyCopyDayPopover({ planId, date, copyLoader }: DailyCopyDayPopoverProps) {
	/** Hooks */
	const { t } = useTranslation();
	const context = usePopoverContext();
	const methods = useForm({
		defaultValues: {
			date: moment(date).getPrevious("work-days").db(),
		},
		resolver: resolver(postCopyDaySchema),
	});

	/** Functions */
	async function handleOnSubmit(json: PostCopyDaySchema) {
		// The column shows "copying day" for as long as this loader runs, and it is the whole
		// column: a refusal — the route's `update Plan` gate, or a board that is not the caller's
		// — makes `responseHandler` throw after toasting, so the loader has to stop in a finally
		// or the day stays a spinner until the page is reloaded.
		copyLoader.start();
		try {
			const response = await client.plan[":planId"].copy.$post({
				param: { planId },
				json,
			});
			await responseHandler(response);
			context.handleOnClose();
		} finally {
			copyLoader.stop();
		}
	}

	/** Render */
	return (
		<form
			className="v-stack gap-4"
			onSubmit={methods.handleSubmit(handleOnSubmit)}
		>
			<DateInput
				control={methods.control}
				name="date"
				label={undefined}
				isRequired
			/>
			<Button
				type="submit"
				variant="primary"
				isLoading={methods.formState.isSubmitting}
			>
				{t("planning.copy_day")}
			</Button>
		</form>
	);
}
